import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { nav, brand } from "../content/site";
import { Button, Container } from "./ui";
import logoFull from "../assets/parkora-logo-full.png";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLinkClick = (e, href) => {
    e.preventDefault();
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      id="top"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 shadow-[0_8px_30px_-12px_rgba(7,27,51,0.2)] backdrop-blur-md"
          : "bg-transparent"
      }`}
    >
      <Container>
        <div
          className={`flex items-center justify-between transition-all duration-300 ${
            scrolled ? "py-2.5" : "py-4"
          }`}
        >
          <a
            href="#top"
            onClick={(e) => handleLinkClick(e, "#top")}
            className="focus-ring flex items-center gap-2 rounded-lg"
            aria-label={`${brand.name} home`}
          >
            <img
              src={logoFull}
              alt={`${brand.name} — ${brand.tagline}`}
              className={`w-auto object-contain transition-all duration-300 ${
                scrolled ? "h-8 sm:h-9" : "h-10 sm:h-11"
              }`}
            />
          </a>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {nav.links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleLinkClick(e, link.href)}
                className="focus-ring rounded-md text-sm font-semibold text-navy/70 transition-colors hover:text-primary"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-4 lg:flex">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-bg-light-orange)] px-3 py-1 text-xs font-bold text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse-slow" />
              {nav.badge}
            </span>
            <Button
              href="#waitlist"
              onClick={(e) => handleLinkClick(e, "#waitlist")}
              size="sm"
            >
              {nav.cta}
            </Button>
          </div>

          <button
            className="focus-ring inline-flex items-center justify-center rounded-xl p-2 text-navy lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </Container>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden border-t border-navy/10 bg-white lg:hidden"
          >
            <Container className="flex flex-col gap-1 py-4">
              {nav.links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleLinkClick(e, link.href)}
                  className="focus-ring rounded-lg px-2 py-3 text-base font-semibold text-navy/80 hover:bg-navy/5"
                >
                  {link.label}
                </a>
              ))}
              <Button
                href="#waitlist"
                onClick={(e) => handleLinkClick(e, "#waitlist")}
                className="mt-3 w-full"
              >
                {nav.cta}
              </Button>
            </Container>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
